import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FiSearch } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import NavGlyph from '../common/NavGlyph';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { USER_ROLES, ROUTES } from '../../constants/constants';
import { createSearchIndex } from './searchIndex';

/* ------------------------------------------------------------------ */
/*  SpotlightSearch                                                   */
/*  A site-wide command palette. The header shows a compact trigger    */
/*  that, on click or Ctrl/Cmd+K, opens a centered modal overlay        */
/*  (rendered in a portal). The trigger hides itself while open.        */
/* ------------------------------------------------------------------ */

export default function SpotlightSearch() {
  const navigate = useNavigate();
  const { companies, selectCompany, openAddCompanyModal, openCompanyDetails } = useApp();
  const { user } = useAuth();

  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);

  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Build role-aware search index (memoised on user role)
  const rawRole = String(user?.roleName || user?.role || '').toLowerCase();
  const isAdmin = ['admin', 'super_admin'].includes(rawRole);
  const effectiveRoles = useMemo(() => {
    const roles = [rawRole];
    // Viewing roles only — a plain admin must not surface super_admin-only
    // pages (Flag Inbox, Admin Alerts, Governance) in search results.
    if (isAdmin) roles.push('professional', 'corporate');
    if (!roles.includes(USER_ROLES.PROFESSIONAL) && !roles.includes(USER_ROLES.CORPORATE)) {
      roles.push(rawRole.includes('corp') ? USER_ROLES.CORPORATE : USER_ROLES.PROFESSIONAL);
    }
    return roles;
  }, [rawRole, isAdmin]);

  const searchIndex = useMemo(() => createSearchIndex(effectiveRoles), [effectiveRoles]);

  // Compute results
  const trimmedQuery = query.trim();
  const groups = useMemo(() => {
    if (!trimmedQuery) return [];
    return searchIndex.search(trimmedQuery, companies);
  }, [trimmedQuery, companies, searchIndex]);

  const quickActions = useMemo(() => searchIndex.getQuickActions(), [searchIndex]);

  // A pasted reference number (DOC-/RES-/MIN-/RPT-XXXXXX) becomes a direct
  // "open this document" result that deep-links into My Documents, where the ref
  // is resolved via the by-ref endpoint (admins included).
  const refLookup = useMemo(() => {
    const m = trimmedQuery.toUpperCase().match(/^(DOC|RES|MIN|RPT)-[0-9A-Z]{6}$/);
    if (!m) return null;
    const ref = m[0];
    return {
      category: 'reference',
      label: 'Reference',
      items: [
        {
          id: `ref-${ref}`,
          label: `Open ${ref}`,
          subtitle: 'Look up document by reference number',
          icon: '📋',
          route: ROUTES.MY_DOCUMENTS,
          _navState: { openDraftRef: ref },
        },
        {
          id: `ref-ai-${ref}`,
          label: `Ask AI about ${ref}`,
          subtitle: 'Open it in the AI chat to discuss or revise',
          icon: '🤖',
          route: ROUTES.AGENT,
          _navState: { openDraftRef: ref },
        },
      ],
    };
  }, [trimmedQuery]);

  // Show quick actions when open with no query
  const displayGroups = useMemo(() => {
    if (trimmedQuery) return refLookup ? [refLookup, ...groups] : groups;
    if (!isOpen) return [];
    return [{ category: 'quick-action', label: 'Quick Actions', items: quickActions }];
  }, [trimmedQuery, groups, isOpen, quickActions, refLookup]);

  // Flat list for keyboard navigation
  const flatItems = useMemo(
    () => displayGroups.flatMap((g) => g.items),
    [displayGroups],
  );

  // Reset highlight when results change
  useEffect(() => {
    setHighlightIndex(0);
  }, [flatItems.length, trimmedQuery]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${highlightIndex}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex]);

  /* ---- Open / close ---- */
  const openPalette = useCallback(() => {
    setIsOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  const closePalette = useCallback(() => {
    setIsOpen(false);
    setQuery('');
  }, []);

  /* ---- Execute a result ---- */
  const executeItem = useCallback(
    (item) => {
      closePalette();

      if (item.action?.type === 'modal' && item.action.target === 'addCompany') {
        openAddCompanyModal();
        return;
      }
      // Picking a company opens the view/edit details modal over the current page.
      if (item.action?.type === 'companyDetails') {
        openCompanyDetails(item.action.target);
        return;
      }
      if (item.action?.type === 'selectCompany') {
        selectCompany(item.action.target);
      }
      if (item.route) {
        navigate(item.route, item._navState ? { state: item._navState } : undefined);
      }
    },
    [navigate, openAddCompanyModal, openCompanyDetails, selectCompany, closePalette],
  );

  /* ---- Keyboard handling inside the palette ---- */
  const handleKeyDown = useCallback(
    (e) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightIndex((prev) => (prev + 1) % Math.max(flatItems.length, 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightIndex((prev) => (prev - 1 + flatItems.length) % Math.max(flatItems.length, 1));
          break;
        case 'Enter':
          e.preventDefault();
          if (flatItems[highlightIndex]) executeItem(flatItems[highlightIndex]);
          break;
        case 'Escape':
          e.preventDefault();
          closePalette();
          break;
        default:
          break;
      }
    },
    [flatItems, highlightIndex, executeItem, closePalette],
  );

  /* ---- Global Ctrl+K / Cmd+K ---- */
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        openPalette();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [openPalette]);

  /* ---- Lock background scroll while open (compensate for the scrollbar
         width so the page doesn't shift/jump when it disappears) ---- */
  useEffect(() => {
    if (!isOpen) return undefined;
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    const prevOverflow = document.body.style.overflow;
    const prevPad = document.body.style.paddingRight;
    document.body.style.overflow = 'hidden';
    if (scrollbar > 0) document.body.style.paddingRight = `${scrollbar}px`;
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPad;
    };
  }, [isOpen]);

  /* ---- Render ---- */
  const hasResults = displayGroups.length > 0;

  // Track flat index across groups for keyboard highlighting
  let flatIdx = -1;

  return (
    <>
      {/* Compact trigger in the header — hides itself while the palette is open */}
      <button
        type="button"
        onClick={openPalette}
        aria-label="Open search — Ctrl K"
        className={`group flex items-center gap-[7px] rounded-xl px-[10px] h-[34px] border-[1.5px] shrink-0 transition-all duration-200 hover:border-[#b8973a] hover:bg-[#f5efe2] active:scale-[0.98] ${isOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        style={{ width: 280, background: '#f8f5ef', borderColor: '#ddd6c6' }}
      >
        <FiSearch size={13} className="shrink-0 text-[#7a7060] transition-all duration-200 group-hover:text-[#b8973a] group-hover:scale-110" />
        <span className="flex-1 text-left text-[11.5px] text-[#7a7060] truncate transition-transform duration-150 group-active:translate-y-[0.5px]">Search anything...</span>
        <kbd
          className="hidden sm:inline-flex items-center text-[9px] px-1.5 py-[1px] rounded border shrink-0"
          style={{ borderColor: '#ddd6c6', color: '#9a9080', background: '#fff', fontFamily: "'JetBrains Mono', monospace" }}
        >
          Ctrl K
        </kbd>
      </button>

      {/* Centered command-palette modal (portal to body) */}
      {isOpen && createPortal(
        <div className="fixed inset-0 z-[1000] flex items-start justify-center px-4 pt-[12vh]">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-[#08111f]/40 backdrop-blur-[3px] animate-in fade-in duration-150"
            onClick={closePalette}
          />

          {/* Panel */}
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Site search"
            className="relative w-full max-w-[640px] rounded-2xl bg-white overflow-hidden flex flex-col animate-in fade-in zoom-in-95 slide-in-from-top-2 duration-150"
            style={{
              border: '1px solid rgba(222,214,198,0.7)',
              boxShadow: '0 24px 64px -12px rgba(8,17,31,0.32), 0 8px 24px -8px rgba(8,17,31,0.18)',
              maxHeight: '70vh',
            }}
          >
            {/* Search input row */}
            <div className="flex items-center gap-3 px-4 h-[56px] border-b shrink-0" style={{ borderColor: '#eee6d8' }}>
              <FiSearch size={18} style={{ color: '#b8973a', flexShrink: 0 }} />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search pages, documents, resolutions, companies..."
                className="flex-1 bg-transparent border-none outline-none text-[15px] text-[#08111f] placeholder:text-[#9a9080]"
                role="combobox"
                aria-expanded={isOpen}
                aria-label="Global search"
                autoComplete="off"
              />
              <kbd
                className="inline-flex items-center text-[9px] px-1.5 py-[2px] rounded border shrink-0"
                style={{ borderColor: '#ddd6c6', color: '#9a9080', background: '#faf8f4', fontFamily: "'JetBrains Mono', monospace" }}
              >
                Esc
              </kbd>
            </div>

            {/* Results — category headers run full-bleed; rows are inset via
                their own margin. Gutter is auto so headers aren't pushed in. */}
            <div ref={listRef} className="cs-menu-scroll overflow-y-auto flex-1" role="listbox" style={{ scrollbarGutter: 'auto' }}>
              {displayGroups.map((group) => (
                <div key={group.category}>
                  {/* Category header */}
                  <div
                    className="text-[9px] uppercase tracking-[2px] font-bold px-4 py-[7px] sticky top-0"
                    style={{ color: '#7a7060', background: '#f8f5ef', borderBottom: '1px solid #eee6d8' }}
                  >
                    {group.label}
                  </div>

                  {/* Items */}
                  {group.items.map((item) => {
                    flatIdx++;
                    const idx = flatIdx;
                    const isHighlighted = idx === highlightIndex;

                    return (
                      <button
                        key={item.id}
                        data-idx={idx}
                        role="option"
                        aria-selected={isHighlighted}
                        className="group mx-2 w-[calc(100%-16px)] flex items-center gap-3 px-2.5 py-[9px] my-[1px] rounded-lg text-left cursor-pointer border-none"
                        style={{
                          background: isHighlighted ? '#f3ebd7' : 'transparent',
                          transition: 'background 80ms ease',
                        }}
                        onClick={() => executeItem(item)}
                        onMouseEnter={() => setHighlightIndex(idx)}
                      >
                        <span
                          className="inline-grid place-items-center w-[28px] h-[28px] rounded-[8px] shrink-0 border transition-transform duration-200 group-hover:scale-105"
                          style={{
                            background: isHighlighted ? '#fff' : '#faf7f2',
                            color: isHighlighted ? '#b8973a' : '#6b6354',
                            borderColor: isHighlighted ? 'rgba(184,151,58,0.35)' : 'rgba(226,217,200,0.65)',
                          }}
                        >
                          <NavGlyph icon={item.icon} size={16} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-[12.5px] font-semibold text-[#08111f] truncate">
                            {item.label}
                          </div>
                          {item.subtitle && (
                            <div className="text-[10.5px] text-[#7a7060] truncate">{item.subtitle}</div>
                          )}
                        </div>
                        <span
                          className="text-[8.5px] px-1.5 py-[1px] rounded-full shrink-0"
                          style={{ background: '#f0ebe2', color: '#7a7060' }}
                        >
                          {group.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ))}

              {/* Empty state */}
              {trimmedQuery && !hasResults && (
                <div className="px-4 py-10 text-center">
                  <div className="text-[14px] text-[#7a7060] mb-1">
                    No results for "<span className="font-semibold text-[#08111f]">{trimmedQuery}</span>"
                  </div>
                  <div className="text-[11px] text-[#9a9080]">
                    Try a different keyword — search pages, documents, forms, resolutions, or companies
                  </div>
                </div>
              )}
            </div>

            {/* Footer hints — clean light key chips */}
            <div
              className="flex items-center gap-5 px-4 py-2 border-t text-[10px] shrink-0"
              style={{ borderColor: '#eee6d8', color: '#9a9080', background: '#faf8f4' }}
            >
              {[
                { keys: ['↑', '↓'], label: 'Navigate' },
                { keys: ['↵'], label: 'Open' },
                { keys: ['Esc'], label: 'Close' },
              ].map((hint) => (
                <span key={hint.label} className="flex items-center gap-1.5">
                  <span className="flex items-center gap-1">
                    {hint.keys.map((k) => (
                      <kbd
                        key={k}
                        className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-md bg-white text-[#7a7060] text-[10px] leading-none border"
                        style={{ borderColor: '#e2d9c8', boxShadow: '0 1px 0 rgba(8,17,31,0.04)' }}
                      >
                        {k}
                      </kbd>
                    ))}
                  </span>
                  {hint.label}
                </span>
              ))}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
